import {
  useId,
  useState,
  useCallback,
  type ComponentPropsWithRef,
  type ChangeEvent,
  forwardRef,
} from "react";
import { type PropsWithOptionalTestID } from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./TextInput.styled";
import { InputLabelTop, type InputLabelTopProps } from "../InputLabelTop";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { OuterField } from "../OuterField";
import { CharactersCounter } from "../CharactersCounter";
import { buildAlertAssociationProps } from "../Alert/AlertAssociationProps";

export type TextInputProps = PropsWithOptionalTestID &
  Omit<
    ComponentPropsWithRef<"input">,
    "value" | "defaultValue" | "style" | "className" | "aria-invalid"
  > & {
    value?: string | number | readonly string[];
    defaultValue?: string | number | readonly string[];
    hasError?: boolean;
    prefix?: string;
    icon?: JSX.Element;
    noCounter?: boolean;
    alertProps?:
      | InputAlertsProps["alerts"][number]
      | InputAlertsProps["alerts"];
    labelProps: Omit<
      InputLabelTopProps,
      "htmlFor" | "labelHintID" | "children"
    >;
  };

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    id,
    hasError,
    prefix,
    icon,
    onChange,
    testID = "input",
    labelProps,
    alertProps,
    noCounter,
    ...inputProps
  } = props;

  const fallbackID = useId();
  const labelHintID = useId();
  const alertId = useId();

  const { labelHint } = labelProps;
  const { maxLength } = inputProps;

  const showCounter = !noCounter && maxLength;

  const {
    tokens: { textInput },
  } = useDesignTokens();

  const [length, setLength] = useState(
    ((inputProps.value || inputProps.defaultValue) as string)?.length || 0
  );

  const safeLabelHintID = labelHint ? labelHintID : undefined;
  const ariaAlertAssociationProps = buildAlertAssociationProps(
    alertId,
    props,
    safeLabelHintID
  );

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      setLength(evt.target.value.length);
      onChange?.(evt);
    },
    [onChange]
  );

  return (
    <InputWrapper
      gap={showCounter ? "4px" : "8px"}
      alert={
        alertProps && (
          <div id={alertId} data-testid={`${testID}-alerts`}>
            <InputAlerts
              alerts={Array.isArray(alertProps) ? alertProps : [alertProps]}
            />
          </div>
        )
      }
    >
      <InputLabelTop
        {...labelProps}
        labelHintID={labelHintID}
        htmlFor={id || fallbackID}
        data-testid={`${testID}-label`}
      >
        <OuterField hasError={hasError}>
          <S.TextInputWrapper>
            {prefix ? (
              <S.Prefix
                variant={textInput.prefix.typography.variant}
                fontFamily={textInput.prefix.typography.fontFamily}
                testID={`${testID}-prefix`}
              >
                {prefix}
              </S.Prefix>
            ) : undefined}
            <S.CustomTextInput
              {...inputProps}
              {...ariaAlertAssociationProps}
              ref={ref}
              id={id || fallbackID}
              onChange={handleChange}
              data-testid={testID}
            />
            {icon ? (
              <S.IconWrapper data-testid={`${testID}-icon-wrapper`}>
                {icon}
              </S.IconWrapper>
            ) : undefined}
          </S.TextInputWrapper>
        </OuterField>
      </InputLabelTop>

      {showCounter ? (
        <CharactersCounter
          length={length}
          maxLength={maxLength}
          testID={`${testID}-characters-counter`}
        />
      ) : undefined}
    </InputWrapper>
  );
});

TextInput.displayName = "TextInput";

export default TextInput;
